import { defineComponent, reactive, SetupContext, readonly, ref, computed, onMounted, toRefs, watch } from '@vue/composition-api';
import CommonCategory from '@/components/common/common-category.component.vue';            // カテゴリ選択共通コンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue'; // 共通メッセージダイアログコンポーネント
import vueRouter from '@/router/index';
import { OfficialDocumentMailType, SimultaneousPullDown, IndividualPullDown, MailDestinationList, TableHeader, DetailList } from '@/shared/type';
import SystemConst from '@/shared/system-const';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import EmailValidator from 'email-validator';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                        // Windowサイズ
    officialDocumentMail: OfficialDocumentMailType                               // メール通知フォーム
    simultaneousPulldown: SimultaneousPullDown[];                                // 送信先プルダウン一覧（一斉通知用）
    individualPulldown: IndividualPullDown[];                                    // 送信先プルダウン一覧（個別通知用）
    mailDestinationList: MailDestinationList[];                                  // 選択済み送信先一覧
    simultaneousMailDestinationDialog: boolean;                                  // 選択済み送信先ダイアログ表示切替（一斉通知用）
    individualMailDestinationDialog: boolean;                                    // 選択済み送信先ダイアログ表示切替（個別通知用）
    tmpTo: string[];                                                             // 送信先一時保存用配列
    displayDetailDialog: boolean;                                                // 選択済み送信先詳細ダイアログ表示切替
    detailNotificationName: string;                                              // 詳細表示する通知先名称
    detailMail: string;                                                          // 詳細表示する通知先メールアドレス
    detailDisplayTableHeaders: TableHeader[];                                    // 一斉通知詳細一覧ヘッダ情報
    displayDetailListItem: DetailList[];                                         // 画面表示する一斉通知詳細一覧
    isLoading: boolean;                                                          // テーブルがロード中: true ロード中でない: false
    validateValues: ValidateType;                                                // バリデーション値
    toErrorMessage: string;                                                      // 送信先のバリデーションメッセージ文字列
    isToValid: boolean;                                                          // 送信先のバリデーション結果
}

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterName: string;                                                                  // パラメーター名
    value: string;                                                                          // パラメーター値
}

/**
 * 公文書メール通知画面コンポーネント
 */
export default defineComponent({
    components: { CommonCategory, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                                // ルータ
        const categoryRef = ref<any>(null);                                      // カテゴリー選択
        const commonRef = ref<any>(null);                                        // 共通メッセージダイアログ
        const tableHeight = SystemConst.TABLE_HEIGHT.OFFICIAL_DOC_MAIL;          // テーブル高さ調整用変数
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                    // 1ページあたりの行数候補（10, 50, 100）
        const itemsPerPage = ref<number>(linesPerPage[0]);                       // 1ページあたりの行数（状態管理用、デフォルト値：10）
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                 // テーブルのデータ取得中に表示するテキスト
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];           // 一斉通知詳細一覧のヘッダ色
        const form = ref<any>(null);
        const formHeight = SystemConst.FORM_HEIGHT.MAIL;                         // フォーム高さ調整用変数
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                          // ウィンドウサイズ
            officialDocumentMail: {
                from: '',                                                        // 送信元
                notificationDivision: 1,                                         // 通知方法
                to: [],                                                          // 送信先
                subject: '',                                                     // 件名
                body: '',                                                        // 本文
                contactSection: '',                                              // 課名
                contactStaff: '',                                                // 担当者
                contactPost: '',                                                 // 職制
                contactTel: '',                                                  // 電話番号
                contactFax: '',                                                  // FAX番号
                contactMail: '',                                                 // メールアドレス
                isPass: 1                                                        // パスワード設定
            },
            simultaneousPulldown: [],                                            // プルダウン表示データ（一斉通知）
            individualPulldown: [],                                              // プルダウン表示データ（個別通知）
            mailDestinationList: [],                                             // 選択済み送信先ダイアログに表示するデータ配列
            simultaneousMailDestinationDialog: false,                            // 選択済み送信先ダイアログ表示切替（一斉通知用）
            individualMailDestinationDialog: false,                              // 選択済み送信先ダイアログ表示切替（個別通知用）
            tmpTo: [],                                                           // 送信先一時保存用配列
            displayDetailDialog: false,                                          // 選択済み送信先詳細ダイアログ表示切替
            detailNotificationName: '',                                          // 詳細表示する通知先名称
            detailMail: '',                                                      // 詳細表示する通知先メールアドレス
            detailDisplayTableHeaders: [],                                       // 一斉通知詳細一覧ヘッダ情報
            displayDetailListItem: [],                                           // 画面表示する一斉通知詳細一覧
            isLoading: false,                                                    // テーブルがロード中: true ロード中でない: false
            validateValues: {},                                                  // バリデーション値
            toErrorMessage: '',                                                  // 添付ファイルのバリデーションメッセージ文字列
            isToValid: true                                                      // 添付ファイルのバリデーション結果
        });

        // 一斉通知詳細一覧ヘッダ情報
        const detailDisplayTableHeaders: TableHeader[] = [
            {
                text: '組織名',
                value: 'orgName',
                width: '30%',
                class: tableHeaderClass
            },
            {
                text: '送信先名',
                value: 'destinationName',
                width: '30%',
                class: tableHeaderClass
            },
            {
                text: '送信先メールアドレス',
                value: 'destinationMail',
                width: '40%',
                class: tableHeaderClass
            }
        ];

        // バリデーション用最大桁数
        const maxLength = {
            // 件名
            subject: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.SUBJECT,
            // 本文
            body: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.BODY,
            // 課名
            contactSection: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.CONTACT_SECTION,
            // 担当者
            contactStaff: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.CONTACT_STAFF,
            // 職制
            contactPost: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.CONTACT_POST,
            // 電話番号
            contactTel: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.CONTACT_TEL,
            // FAX番号
            contactFax: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.CONTACT_FAX,
            // メールアドレス
            contactMail: SystemConst.OFFICIAL_DOCUMENT_MAIL_MAX_LENGTH.CONTACT_MAIL
        }

        // 送信先のバリデーション
        const toRules = [
            () => {
                // バリデーションは初期表示APIの結果(m_parametersテーブル)を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('destinationCount' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const maxCount = Number(state.validateValues['destinationCount'].value);

                // 選択済み送信先が1以上、上限以下であるか確認する
                if (state.tmpTo.length === 0) {
                    return CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['送信先']);
                }
                if (state.tmpTo.length > maxCount) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, [
                        "送信先",
                        state.validateValues['destinationCount'].value + '件以下']);
                }
                return true;
            }
        ];

        // 件名のバリデーション
        const subjectRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['件名']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['subject'], '件名')
        ];

        // 本文のバリデーション
        const bodyRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['本文']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['body'], '本文')
        ]

        // 課名のバリデーション
        const contactSectionRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['課名']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['contactSection'], '課名')
        ];

        // 担当者のバリデーション
        const contactStaffRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['担当者']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['contactStaff'], '担当者')
        ];

        // 職制のバリデーション
        const contactPostRules = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['contactPost'], '職制')
        ];

        // 電話番号のバリデーション
        const contactTelRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['電話番号']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['contactTel'], '電話番号'),
            // 属性チェック
            (v: any) =>
                /^[0-9-]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['電話番号', '数字、ハイフン（-）のみ']),
            // 属性チェック(数字のみまたは（数字）-（数字）-（数字）の形式であるかのチェック)
            (v: any) =>
                /^[0-9]+$|^[0-9]+-[0-9]+-[0-9]+$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['電話番号', '数字または（数字）-（数字）-（数字）の形式'])
        ];

        // FAX番号のバリデーション
        function contactFaxRules() {
            const errorMsgs = [];
            const v = state.officialDocumentMail.contactFax;  // 入力されたFAX番号
            if (v !== '' && v !== null) {
                // 未入力でなく、nullでない場合
                // 属性チェック
                if (!/^[0-9-]*$/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX, ['FAX番号', '数字、ハイフン（-）のみ']));
                }
                // 属性チェック(数字のみまたは（数字）-（数字）-（数字）の形式であるかのチェック)
                if (!/^[0-9]+$|^[0-9]+-[0-9]+-[0-9]+$/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX, ['FAX番号', '数字または（数字）-（数字）-（数字）の形式']));
                }
                // 桁数チェック
                const resultCheckLength = CommonFunction.checkLength(v, maxLength['contactFax'], 'FAX番号');
                if (typeof resultCheckLength === 'string') {
                    // エラーメッセージが返ってきた場合
                    errorMsgs.push(resultCheckLength);
                }
            }
            return errorMsgs;
        }

        // メールアドレスのバリデーション
        function contactMailRules() {
            const errorMsgs = [];
            const v = state.officialDocumentMail.contactMail;  // 入力されたメールアドレス
            if (v !== '' && v !== null) {
                // 未入力でなく、nullでない場合
                // 属性チェック
                if (!/^[0-9a-z-_'.@]*$/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX, ['メールアドレス', '小文字の英字、数字、ピリオド（.）、ハイフン（-）、アンダースコア（_）、アポストロフィ（\'）のみ']));
                }
                // メールアドレスバリデーション
                const resultCheckMail = EmailValidator.validate(v) ||
                    CommonFunction.replaceArray(MessageConst.E_EMAIL, ['メールアドレス']);
                if (typeof resultCheckMail === 'string') {
                    // エラーメッセージが返ってきた場合
                    errorMsgs.push(resultCheckMail);
                }

                // 桁数チェック
                const resultCheckLength = CommonFunction.checkLength(v, maxLength['contactMail'], 'メールアドレス');
                if (typeof resultCheckLength === 'string') {
                    // エラーメッセージが返ってきた場合
                    errorMsgs.push(resultCheckLength);
                }

                // 先頭・末尾のハイフンチェック
                const resultCheckHyphen = checkHyphen(v, 'ハイフン（-）');
                if (typeof resultCheckHyphen === 'string') {
                    // エラーメッセージが返ってきた場合
                    errorMsgs.push(resultCheckHyphen);
                }
            }
            return errorMsgs;
        }

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.officialDocument);
        // 値に変更があった場合
        watch(activeNode, (newVal, oldVal) => {
            // 初期表示でない場合
            if (oldVal.length !== 0) {
                // 公文書一覧（一般）画面へ遷移
                selectCategory();
            }
        })

        /**
         * 初期表示
         */
        onMounted(() => {
            init();
        });
        async function init() {
            // カテゴリ取得
            await categoryRef.value.getCategory();

            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-email-send');

            // mainTitleを「メール通知」に変更
            await ctx.root.$store.commit('setMainTitle', 'メール通知');

            // 一斉通知詳細一覧ヘッダ情報設定
            state.detailDisplayTableHeaders = detailDisplayTableHeaders;

            // メール通知対象公文書のID取得
            const officialDocId = computed(() => ctx.root.$route.params.id);

            // カテゴリのID取得
            const orgId = ctx.root.$store.state.officialDocument.activeNode[0];

            // APIで初期表示データを取得
            const response = await ctx.root.$store.dispatch('officialDocument/getMailInitParameters',
                {
                    organizationId: orgId,
                    officialDocumentId: officialDocId.value
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.BAD_REQUEST) {
                    // 404エラーの場合、エラーメッセージを表示して公文書一覧（管理）画面へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/official-documents/admin/list');
                    return;
                }

                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 送信元を設定する
            state.officialDocumentMail.from = response.data.from;
            // 件名を設定する
            state.officialDocumentMail.subject = response.data.subject;
            // 本文を設定する
            state.officialDocumentMail.body = response.data.body;
            // 課名を設定する
            state.officialDocumentMail.contactSection = response.data.contactSection;
            // 職制を設定する
            state.officialDocumentMail.contactPost = response.data.contactPost;

            // バリデーション値を設定する
            state.validateValues = response.data.validateParam;

            // 電話番号のデータがある場合
            if (response.data.contactTel) {
                // 電話番号を設定する
                state.officialDocumentMail.contactTel = response.data.contactTel;
            }
            
            // 送信先（一斉送信）を設定する
            state.simultaneousPulldown = response.data.simultaneousPulldown;
            for (let i = 0; i < state.simultaneousPulldown.length; i++) {
                // 取得した通知先名称とメールアドレスでプルダウン表示用のデータを作成する
                state.simultaneousPulldown[i]['text'] = '【' + state.simultaneousPulldown[i]['notificationName'] + '】' + state.simultaneousPulldown[i]['value'];
            }
            // 送信先（個別送信）を設定する
            state.individualPulldown = response.data.individualPulldown;
            for (let i = 0; i < state.individualPulldown.length; i++) {
                // 取得した送信先の組織型名と組織名でプルダウン表示用のデータを作成する
                state.individualPulldown[i]['text'] = '【' + state.individualPulldown[i]['orgTypeName'] + '】' + state.individualPulldown[i]['orgName'];
            }
        }

        /**
         * カテゴリ選択時のイベント
         */
        function selectCategory() {
            // 公文書一覧（一般）画面へ遷移
            router.push('/official-documents/list');
        }

        /**
         * ハイフンチェック
         * 
         * @param address       入力されたデータ
         * @param message       表示メッセージ
         * @returns string      異常値ならエラーメッセージ
         *          boolean     正常値ならtrue 
         */
        function checkHyphen(address: string, message: string): string | boolean {
            // 末尾のハイフンのチェック
            if (/-$/.test(address)) {
                return CommonFunction.replaceArray(MessageConst.E_ADDRESS_END_ERR, [message]);
            }

            return true;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        /**
         * 送信の確認を行い、確認メッセージダイアログを表示する
         */
        function displayDialog() {
            // フロントのバリデーションチェックをする
            updateTo();
            if (!form.value.validate() || !state.isToValid) {
                // バリデーションチェックにエラーがある場合
                // バリデーションエラーの際にフォームの一番上にスクロールする
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }
                return;
            }

            // 送信先の初期化
            state.officialDocumentMail.to = [];

            // 送信先一時保存配列の内容をフォームの送信先に格納
            if (state.officialDocumentMail.notificationDivision === 1) {
                // 一斉通知の場合
                // 送信先一時保存用配列のデータを、リクエストパラメータに設定するstate.officialDocument.toに格納する
                for (let i = 0; i < state.tmpTo.length; i++) {
                    // 送信先一時保存用配列（state.tmpTo）の件数分繰り返す
                    state.officialDocumentMail.to.push({ mail: state.tmpTo[i], orgId: '' });
                }
            } else {
                // 個別通知の場合
                // 送信先一時保存用配列のデータを、リクエストパラメータに設定するstate.officialDocument.toに格納する
                for (let i = 0; i < state.individualPulldown.length; i++) {
                    // 送信先一時保存用配列（state.tmpTo）の件数分繰り返す
                    if (state.tmpTo.indexOf(state.individualPulldown[i].value) !== -1) {
                        // 選択した送信先のメールアドレス、組織IDを設定
                        state.officialDocumentMail.to.push(
                            { mail: state.individualPulldown[i].value, orgId: state.individualPulldown[i].id }
                        );
                    }
                }
            }

            // 通知処理実行確認ダイアログに表示するメッセージを格納する
            let displayMessage = '';
            displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['メール通知']);

            // 確認メッセージダイアログ表示を表示する
            commonRef.value.displayConfirmationMessageDialog(displayMessage);

            // 「はい」をクリックした場合はprocessingData()メソッドを呼び出す
            // 「いいえ」をクリックした場合はダイアログを閉じる
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {
            // メール通知対象公文書のID取得
            const officialDocId = computed(() => ctx.root.$route.params.id);

            // 公文書メール通知 メール作成APIでメールを送付する
            // POST /api/official-documents/mail-create
            const response = await ctx.root.$store.dispatch(
                'officialDocument/createMail',
                {
                    officialDocumentMail: state.officialDocumentMail,
                    officialDocumentId: officialDocId.value
                }
            );

            // HTTPステータスコードが200以外の場合
            // エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // HTTPステータスコードが200の場合
            // 通知完了メッセージダイアログ表示
            commonRef.value.displayMessageTransitionDialog(response, '/official-documents/admin/list');
        }

        /**
         * 送信先選択のchip削除
         */
        function removeChip(item: any) {
            state.tmpTo.splice(state.tmpTo.indexOf(item.value), 1)
            state.tmpTo = [...state.tmpTo]

            // 送信先バリデーション実施
            updateTo();
        }

        /**
         * 送信先選択で選択したデータを、送信先確認ダイアログで表示する配列に格納
         */
        function selectPulldown() {
            // 送信先確認ダイアログで表示する配列を初期化
            state.mailDestinationList = [];
            let j = 0;

            // 送信先が選択されているか
            if (state.tmpTo.length !== 0) {
                if (state.officialDocumentMail.notificationDivision === 1) {
                    // 一斉通知の場合
                    for (let i = 0; i < state.simultaneousPulldown.length; i++) {
                        // 送信先データの数だけ繰り返す
                        if (state.tmpTo.indexOf(state.simultaneousPulldown[i].value) !== -1) {
                            // 選択したデータを選択先確認リストの配列に格納
                            state.mailDestinationList[j++] = {
                                notificationName: state.simultaneousPulldown[i].notificationName,
                                mail: state.simultaneousPulldown[i].value
                            }
                        }
                    }
                } else {
                    // 個別通知の場合
                    for (let i = 0; i < state.individualPulldown.length; i++) {
                        // 送信先データの数だけ繰り返す
                        if (state.tmpTo.indexOf(state.individualPulldown[i].value) !== -1) {
                            // 選択したデータを選択先確認リストの配列に格納
                            state.mailDestinationList[j++] = {
                                orgTypeName: state.individualPulldown[i].orgTypeName,
                                orgName: state.individualPulldown[i].orgName
                            }
                        }
                    }
                }
            }
        }

        /**
         * 選択した一斉通知先の詳細を表示する処理
         * 
         * @param notificationName 選択した通知先名称
         * @param groupKey 選択したメールアドレス
         */
        async function getDestinationDetail(notificationName: string, groupKey: string) {
            // 一斉通知詳細の一覧情報を初期化する
            state.displayDetailListItem = [];

            // タイトル表示用変数に選択した通知先名称と選択したメールアドレスをそれぞれ格納する
            state.detailNotificationName = notificationName;
            state.detailMail = groupKey;

            // 選択済み送信先詳細ダイアログの表示をtrueにする
            state.displayDetailDialog = true;
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // 一斉通知詳細一覧取得APIでデータを取得する
            // GET /api/official-documents/destination-detail
            // パラメータ：選択したメールアドレス
            const response = await ctx.root.$store.dispatch('officialDocument/getDestinationDetail',
                {
                    groupKey: groupKey
                }
            );

            // API通信のレスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得した一斉通知詳細一覧の全データを設定する
            state.displayDetailListItem = response.data.destinationDetailLists;

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * v-chipのテキストが一定以上の長さであれば省略する処理
         * 
         * @param value chipに表示するテキスト
         */
        function getOmitString(value: string) {
            const length = 40;
            if (value.length <= length) {
                return value;
            }
            return value.substring(0, length) + "...";
        }

        /**
         * 送信先が追加・削除された時のイベント
         */
        function updateTo() {
            // 送信先のバリデーションを行う
            for (let index = 0; index < toRules.length; index++) {
                const rule: any = toRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.toErrorMessage = valid;
                    state.isToValid = false;
                    break;
                } else {
                    // エラーなし
                    state.toErrorMessage = '';
                    state.isToValid = true;
                }
            }
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            router.push('/official-documents/admin/list');
        }

        return {
            tableHeight,
            linesPerPage,
            itemsPerPage,
            tableLoadingText,
            state: readonly(state),
            categoryRef,
            commonRef,
            form,
            maxLength,
            subjectRules,
            bodyRules,
            contactSectionRules,
            contactStaffRules,
            contactPostRules,
            contactTelRules,
            formHeight,
            contactFaxRules,
            contactMailRules,
            displayDialog,
            processingData,
            removeChip,
            selectPulldown,
            onResize,
            getDestinationDetail,
            getOmitString,
            updateTo,
            back
        };
    }
});

